import { ArrowLeftOutlined, FilePdfFilled, LoadingOutlined } from "@ant-design/icons";
import { Button, Card, Empty, List, Tabs, Tag, Tooltip } from "antd";
import { t } from "i18next";
import React, { useContext, useEffect, useState } from "react";
import { ChooseOrView, COMPARISON_COLORS, ComparisonSlot } from ".";
import { SessionContext } from "../../App";
import {
  GetReportRequest,
  GetReportRequestLayout,
  GetSurveyCampaignsResponse,
  GetSurveyCampaignGroupReportResponse
} from "../../constants/apiRequestResponse";
import TEST_IDS from "../../constants/cypressTestIds";
import { COLORS } from "../../constants/misc";
import { FeatureFlagStatus, FeatureFlagTemplateKey } from "../../constants/types";
import { getReport, getSurveyCampaigns } from "../../services/api";
import { FeedbackGroupQuestionReportKeys } from "../../services/i8tn/FeedbackReportView/keys";
import { getQueryParam, parseDate, setQueryParam } from "../../util";
interface Prop {
  report?: GetSurveyCampaignGroupReportResponse;
}

export default function FeedbackGroupView(props: Prop) {
  const context = useContext(SessionContext);
  const [report, setReport] = useState("");
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [isPrintAsPDFLoading, setIsPrintAsPDFLoading] = useState(false);
  const [comparedTo, setComparedTo] = useState<string | undefined>(getQueryParam("group.comparedTo") || undefined);
  const [comparisonReport, setComparisonReport] = useState("");
  const [isComparisonLoading, setIsComparisonLoading] = useState(false);
  const currentCampaignId = getQueryParam("surveyCampaignId") || "";
  const [
    getSurveyCampaignsByCompanyIdResponse,
    setGetSurveyCampaignsByCompanyIdResponse,
  ] = useState<GetSurveyCampaignsResponse | undefined>();
  const [chooseOrView, setCompareView] = useState<ChooseOrView | undefined>(getQueryParam("group.chooseOrView") as ChooseOrView || undefined)

  useEffect(() => {
    if (!context?.company?._id) return;

    getSurveyCampaigns({
      type: props.report?.surveyTemplate?.type,
      createdBefore: props.report?.surveyCampaign?.createdAt
    })
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        setGetSurveyCampaignsByCompanyIdResponse(res);
      })

    setIsReportLoading(true);
    const req: GetReportRequest = {
      surveyCampaignId: currentCampaignId,
      format: "html",
      layout: GetReportRequestLayout.Group,
    };
    getReport(req)
      .then((res) => {
        setReport(res?.data);
      })
      .finally(() => setIsReportLoading(false));
  }, [context?.company?._id, currentCampaignId, props.report]);

  useEffect(() => {
    if (!comparedTo) return;

    setIsComparisonLoading(true);
    const req: GetReportRequest = {
      surveyCampaignId: currentCampaignId,
      format: "html",
      layout: GetReportRequestLayout.Group,
      comparedTo: comparedTo,
    };
    getReport(req)
      .then((res) => {
        setComparisonReport(res?.data);
      })
      .catch((err) => {
        setComparisonReport(err);
      })
      .finally(() => setIsComparisonLoading(false));
  }, [comparedTo, currentCampaignId]);

  const onExportPDF = () => {
    setIsPrintAsPDFLoading(true);
    const req: GetReportRequest = {
      surveyCampaignId: currentCampaignId,
      format: "pdf",
      layout: GetReportRequestLayout.Group,
      comparedTo: comparedTo
    };
    getReport(req)
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsPrintAsPDFLoading(false));
  };

  const isEnableBehavioralChangeFeature = context?.featureFlags?.find((ff) => {
    return (
      ff.key === FeatureFlagTemplateKey.ENABLE_BEHAVIORAL_CHANGE &&
      ff.status === FeatureFlagStatus.ENABLED
    );
  })
    ? true
    : false;

  if (isEnableBehavioralChangeFeature) {
    return (
      <div style={{ display: "flex", width: "100%", justifyContent: "center" }}>
        <Card style={{ width: "60%", minHeight: 500 }} >
          <Tabs defaultActiveKey={getQueryParam('group.currentOrCompare')}
            onChange={key => {
              setQueryParam('group.currentOrCompare', key)
            }}
          >
            <Tabs.TabPane tab={t(FeedbackGroupQuestionReportKeys.feedbackReport_currentReportTab)} key="current">

              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                {isPrintAsPDFLoading ? (
                  <LoadingOutlined style={{ fontSize: 24 }} />
                ) : (
                  <Tooltip title={t(FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip)}>
                    <FilePdfFilled
                      data-testid={TEST_IDS.REPORT_PAGE.group_download_as_pdf_button_id}
                      onClick={() => onExportPDF()}
                      style={{ fontSize: 24, cursor: "pointer" }}
                    />
                  </Tooltip>
                )}
              </div>
              {isReportLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                  <LoadingOutlined spin={true} style={{ fontSize: 32, color: COLORS.PRIMARY_COLOR }} />
                </div>
              ) : (
                  <div dangerouslySetInnerHTML={{ __html: report }}></div>
              )}
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={
                <span>
                  {t(FeedbackGroupQuestionReportKeys.feedbackReport_behaviouralChangeReportTab)}
                  <Tag color="green" style={{ marginLeft: 4 }}>
                    {t(FeedbackGroupQuestionReportKeys.feedbackReport_newTag)}
                  </Tag>
                </span>
              }
              key="compare"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* Past vs Present slots - shared between both views */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr", gap: 8, alignItems: "center" }}>
                  <ComparisonSlot
                    pastOrPresent="past"
                    surveyCampaign={getSurveyCampaignsByCompanyIdResponse?.surveyCampaigns.find(sc => sc._id === comparedTo)
                    }
                    showClear={chooseOrView === "choose"}
                    onClear={() => {
                      setComparedTo(undefined);
                      setQueryParam("group.comparedTo", "");
                    }}
                  />
                  <div style={{ textAlign: "center", fontSize: 12, color: "#bfbfbf", fontWeight: 500 }}>{t(FeedbackGroupQuestionReportKeys.feedbackReport_vsLabel)}</div>
                  <ComparisonSlot
                    pastOrPresent="present"
                    surveyCampaign={props.report?.surveyCampaign}
                    showClear={false}
                  />
                </div>

                {chooseOrView === "view" ? (
                // Results view
                  <>
                    {/* Action bar */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: "8px 0",
                      borderBottom: "1px solid #f0f0f0",
                    }}>
                      <Button
                        type="link"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                          setCompareView("choose");
                          setQueryParam("group.chooseOrView", "choose");
                        }}
                      >
                        {t(FeedbackGroupQuestionReportKeys.feedbackReport_changeSelectionButton)}
                      </Button>
                      {isPrintAsPDFLoading ? (
                        <LoadingOutlined style={{ fontSize: 24 }} />
                      ) : (
                        <Tooltip title={t(FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip)}>
                          <FilePdfFilled
                            data-testid={TEST_IDS.REPORT_PAGE.group_download_as_pdf_button_id}
                            onClick={() => onExportPDF()}
                              style={{ fontSize: 24, cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </div>

                    {isComparisonLoading ? (
                      <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                        <LoadingOutlined spin={true} style={{ fontSize: 32, color: COLORS.PRIMARY_COLOR }} />
                      </div>
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: comparisonReport }}></div>
                    )}
                  </>
                ) : (
                  // Picker view
                    <>
                    {/* Campaign list */}
                    <List
                      dataSource={getSurveyCampaignsByCompanyIdResponse?.surveyCampaigns.filter((sc) => sc._id !== currentCampaignId)}
                      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t(FeedbackGroupQuestionReportKeys.feedbackReport_noPreviousCampaignsFound)} /> }}
                      renderItem={(c) => (
                        <List.Item
                          data-testclass='compareto-survey-campaigns-list'
                          key={c._id}
                          onClick={() => {
                            setQueryParam("group.comparedTo", c._id);
                            setComparedTo(c._id); // Preload the comparison report
                          }}
                          style={{
                            cursor: "pointer",
                            background: comparedTo === c._id ? COMPARISON_COLORS.past.background : undefined,
                            paddingLeft: 16,
                            paddingRight: 16,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span>{c.name}</span>
                            </div>
                            <p style={{ color: "grey" }}>{parseDate(c.createdAt)}</p>
                          </div>
                        </List.Item>
                      )}
                    />

                    {/* Compare button */}
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        type="primary"
                          disabled={!comparedTo}
                          onClick={() => {
                            setQueryParam("group.chooseOrView", "view");
                            setCompareView("view")
                        }}
                      >
                        {t(FeedbackGroupQuestionReportKeys.feedbackReport_compareButton)}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </div>
    );
  } else {
    return (
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-around",
        }}
      >
        <Card
          style={{
            width: "60%",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              gap: 20,
              marginBottom: 50,
            }}
          >
            <div>
              <p style={{ fontWeight: "bold" }}>
                {props.report?.surveyCampaign?.name}
              </p>
              <p
                style={{ color: "grey" }}
                data-testid={TEST_IDS.REPORT_PRINT_PAGE.survey_template_name}
              >
                {props.report?.surveyTemplate?.name}
              </p>
            </div>
            {isPrintAsPDFLoading ? (
              <div>
                <LoadingOutlined style={{ fontSize: 30 }} />
              </div>
            ) : (
              <Tooltip
                title={t(
                  FeedbackGroupQuestionReportKeys.feedbackGroupQuestionReport_downloadAsPDFTooltip
                )}
              >
                <FilePdfFilled
                  data-testid={
                    TEST_IDS.REPORT_PAGE.group_download_as_pdf_button_id
                  }
                  onClick={(e) => {
                    onExportPDF();
                  }}
                  style={{ fontSize: 30 }}
                />
              </Tooltip>
            )}
          </div>

          <div dangerouslySetInnerHTML={{ __html: report }}></div>
        </Card>
      </div>
    );
  }
}